import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

function GoogleCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [status, setStatus] = useState('Processing...');
    const hasProcessed = useRef(false); // Prevent double-calling in StrictMode

    useEffect(() => {
        // Prevent double execution in React StrictMode
        if (hasProcessed.current) return;

        const code = searchParams.get('code');
        const state = searchParams.get('state') || 'login'; // 'login' or 'register'

        if (code) {
            hasProcessed.current = true;
            handleGoogleCallback(code, state);
        } else {
            setError('No authorization code received');
        }
    }, [searchParams]);

    const handleAuthSuccess = (data) => {
        if (data.access_token) localStorage.setItem('token', data.access_token);
        if (data.role) localStorage.setItem('role', data.role);
        if (data.accessible_modules) localStorage.setItem('accessible_modules', JSON.stringify(data.accessible_modules));
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));

        const role = (data.role || '').toLowerCase();
        const baseRole = (data.base_role || '').toLowerCase();

        if (role === 'university' || baseRole === 'university') navigate('/uni-dashboard');
        else if (role === 'pop' || baseRole === 'pop') navigate('/pop-dashboard');
        else if (role === 'admin' || baseRole === 'admin') navigate('/admin-dashboard');
        else if (role === 'student' || baseRole === 'student') navigate('/student-dashboard');
        else if (role === 'faculty' || baseRole === 'faculty') navigate('/faculty-dashboard');
        else if (role === 'tpo' || baseRole === 'tpo') navigate('/tpo-dashboard');
        else if (role === 'compliance' || baseRole === 'compliance') navigate('/compliance-dashboard');
        else if (data.permissions || baseRole === 'custom_role' || role === 'custom_role') navigate('/uni-dashboard');
        else navigate('/');
    };

    const handleGoogleCallback = async (code, context) => {
        try {
            setStatus('Verifying with Google...');
            const response = await api.post('/auth/google/callback', { code, context });

            // User needs to complete registration (new Google user)
            if (response.data.needs_role_selection) {
                // Store Google user data and redirect to role selection
                sessionStorage.setItem('google_user', JSON.stringify({
                    email: response.data.email,
                    name: response.data.name,
                    oauth_provider: 'google'
                }));
                navigate('/register?oauth=google');
                return;
            }

            // User already registered - log them in (for register flow)
            if (response.data.already_registered && response.data.access_token) {
                handleAuthSuccess(response.data);
                return;
            }

            // User needs to register first (tried to login but not registered)
            if (response.data.needs_registration) {
                setError('No account found. Please sign up first.');
                setTimeout(() => navigate('/register'), 2000);
                return;
            }

            if (response.data.access_token) {
                handleAuthSuccess(response.data);
            }
        } catch (err) {
            console.error('OAuth callback error:', err);
            const errorMessage = err.response?.data?.detail || 'Google authentication failed';
            setError(errorMessage);
        }
    };

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                fontFamily: 'Inter, sans-serif'
            }}>
                <div style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '20px 40px',
                    borderRadius: '12px',
                    textAlign: 'center'
                }}>
                    <h2>Login Failed</h2>
                    <p>{error}</p>
                    <a href="/login" style={{ color: '#0891b2' }}>Back to Login</a>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            fontFamily: 'Inter, sans-serif'
        }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    width: '50px',
                    height: '50px',
                    border: '3px solid var(--border)',
                    borderTopColor: '#0891b2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                }}></div>
                <h2 style={{ color: 'var(--proto-white)' }}>{status}</h2>
                <p style={{ color: 'var(--text-muted)' }}>Please wait...</p>
            </div>
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default GoogleCallback;

